// src/components/story-composer.tsx
//
// Story creation: POST contents with isStory: true in the body, per the
// endpoint contract. Deliberately a separate, smaller composer rather than
// bolting a story mode onto PostComposer — a story is one photo/video (no
// polls, no scheduling, no mentions), and PostComposer's complexity around
// those features doesn't apply here.
//
// Reuses the same upload/mutation plumbing PostComposer already uses
// (useFileQueue, useUploadFiles, useCustomMutation against the same
// `contents` endpoint, useInvalidateContent — which now also covers the
// "GetStories" query, see useContentInteractions.ts) so a freshly-posted
// story shows up in the feed's story rail immediately.

import { useRef, useState } from "react";
import { Avatar, Icon } from "@/lib/ui";
import { useCustomMutation } from "@/hooks/api/use-api";
import { useUploadFiles } from "@/hooks/useUploadFiles";
import { getMediaType } from "@/utils/helper";
import { useAppSelector } from "@/services/hook";
import type { RootState } from "@/services/store";
import { useFetchProfile } from "@/hooks/apiHooks";
import { useNotify } from "@/hooks/useNotify";
import { ACCEPT_ATTR, useFileQueue } from "@/hooks/useFileQueue";
import { useInvalidateContent } from "@/hooks/useContentInteractions";
import { MediaAttachments } from "@/components/media-attachements";

export function StoryComposer({
  onClose,
  onPosted,
}: {
  onClose: () => void;
  onPosted?: () => void;
}) {
  const notify = useNotify();
  const invalidate = useInvalidateContent();
  const { userObject } = useAppSelector((s: RootState) => s.auth);
  const { data: myProfile } = useFetchProfile(userObject);
  const myProfileImageUrl = (myProfile as { data?: { profileImageUrl?: string } } | undefined)
    ?.data?.profileImageUrl;
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [text, setText] = useState("");
  const [dragging, setDragging] = useState(false);
  const { files, addFiles, removeFile, clear, totalMB } = useFileQueue();

  const {
    uploadFiles,
    isUploading,
    reset: resetUpload,
  } = useUploadFiles({
    usid: userObject?.usid,
    onError: (error: any) => notify.error(error?.message || "Upload failed"),
  });

  const createStory = useCustomMutation({
    endpoint: "contents",
    onSuccessCallback: () => {
      invalidate();
      setText("");
      clear();
      resetUpload();
      onPosted?.();
      onClose();
    },
    successMessage: () => "Added to your story",
    onError: (err: any) =>
      notify.error(err?.response?.data?.message || "An error occurred"),
  });

  const busy = createStory.isPending || isUploading;

  // A story is one frame — one photo or video. Picking a new file while one
  // is already queued swaps it instead of adding a second, so this can't
  // turn into a multi-media post by accident.
  const pickFiles = (incoming: File[]) => {
    if (!incoming.length) return;
    clear();
    addFiles(incoming.slice(0, 1));
  };

  const canPost = files.length > 0 && !busy;

  const submit = async () => {
    if (files.length === 0) {
      notify.error("Add a photo or video to your story");
      return;
    }
    if (busy) return;
    try {
      const mediaLinks = await uploadFiles(files);
      createStory.mutate({
        message: text.trim(),
        mentions: [],
        scheduledFor: null,
        mediaFiles: mediaLinks,
        mediaType: getMediaType(files),
        isStory: true,
      });
    } catch (error) {
      console.error("Story upload failed:", error); // useUploadFiles' onError already told the user
    }
  };

  return (
    <div
      className="col gap12"
      style={{
        outline: dragging ? "2px solid var(--blue-ink)" : undefined,
        outlineOffset: -2,
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setDragging(true);
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        pickFiles(Array.from(e.dataTransfer.files));
      }}
    >
      <div className="row gap12" style={{ alignItems: "flex-start" }}>
        <Avatar name="You" src={myProfileImageUrl} size={40} initialsOnly />
        <textarea
          className="input"
          placeholder="Add a caption… (optional)"
          rows={2}
          autoFocus
          value={text}
          style={{ resize: "none", width: "100%" }}
          onChange={(e) => setText(e.target.value)}
        />
      </div>

      {files.length > 0 ? (
        <div style={{ paddingLeft: 52 }}>
          <MediaAttachments
            files={files}
            onRemove={removeFile}
            totalMB={totalMB}
            maxMB={10}
          />
        </div>
      ) : (
        <button
          type="button"
          className="col center gap8"
          style={{
            marginLeft: 52,
            height: 160,
            borderRadius: 12,
            border: "1px dashed var(--line2)",
            cursor: "pointer",
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <Icon n="camera" s={26} c="var(--muted)" />
          <span className="muted t13">Click or drag a photo/video here</span>
        </button>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTR}
        hidden
        onChange={(e) => {
          pickFiles(Array.from(e.target.files ?? []));
          e.target.value = "";
        }}
      />

      <div className="row" style={{ justifyContent: "flex-end", marginTop: 4 }}>
        <div className="row gap10">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-blue btn-sm"
            disabled={!canPost}
            onClick={submit}
          >
            {busy ? "Sharing…" : "Share to story"}
          </button>
        </div>
      </div>
    </div>
  );
}
